import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import posthog from 'posthog-js';
import { type UserProfile, api } from '@/lib/api';
import { useTrtcChatStore } from './useTrtcChatStore';
import { isInAppWebView } from '@/lib/utils';

// 定义用户状态类型
export interface UserState {
  // 用户状态
  token: string | null;
  address: string | null;
  userId: number | null;
  userInfo: UserProfile | null;
  // evm
  evmAddress: string | null;
  // solana
  solanaAddress: string | null;

  userInfoLoading: boolean;
  // 操作方法
  setToken: (token: string) => void;
  setAddress: (address: string) => void;
  setEvmAddress: (evmAddress: string) => void;
  setSolanaAddress: (solanaAddress: string) => void;
  clearUserInfo: () => void;
  isLoggedIn: () => boolean;
  setUserInfo: (userInfo: UserProfile) => void;
  setUserId: (userId: number) => void;
  fetchUserInfo: () => Promise<void>;
}

// 创建 Zustand store
export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      // 初始状态
      token: null,
      address: null,
      userId: null,
      userInfo: null,
      evmAddress: null,
      solanaAddress: null,
      userInfoLoading: false,
      setUserId: (userId: number) => {
        set({ userId });
      },
      // 设置用户信息
      setUserInfo: (userInfo: UserProfile) => {
        set({ userInfo });
      },

      // 设置 token
      setToken: (token: string) => {
        set({ token });
      },

      // 设置地址
      setAddress: (address: string) => {
        set({ address });
      },

      // 设置 evm 地址
      setEvmAddress: (evmAddress: string) => {
        set({ evmAddress });
      },

      // 设置 solana 地址
      setSolanaAddress: (solanaAddress: string) => {
        set({ solanaAddress });
      },

      // 清除用户信息
      clearUserInfo: () => {
        if (isInAppWebView()) return;
        // 登出腾讯聊天 SDK
        useTrtcChatStore.getState().logout();
        try {
          api.user.logout();
        } catch (err) {
          console.error('退出登录失败:', err);
        }
        // 清除API客户端的token和所有用户状态
        set({
          token: null,
          address: null,
          userInfo: null,
          userId: null,
          userInfoLoading: false,
          evmAddress: null,
          solanaAddress: null,
        });
      },

      // 检查是否已登录
      isLoggedIn: () => {
        const state = get();
        return !!state.token;
      },

      // 获取用户信息
      fetchUserInfo: async () => {
        const state = get();
        if (!state.token) {
          return;
        }
        // 获取本人登录的地址
        set({ userInfoLoading: true });
        try {
          const [userInfo, userPointsUserList] = await Promise.all([
            api.user.getUserInfo(),
            api.user.getMyRecommendations({
              page: 1,
              limit: 500,
            }),
          ]);

          if (userInfo.code === 200) {
            // 登录成功，合并用户信息与推荐列表
            const userInfoData = userInfo?.data as unknown as UserProfile;
            // 获取当前用户的实际网络
            const currentWalletAddress =
              (userInfoData.walletNetwork.toLocaleLowerCase() === 'solana' ? userInfoData.solWalletAddress : userInfoData.evmWalletAddress) ||
              userInfoData.walletAddress;
            const userPointsUserListData = userPointsUserList?.data?.list || [];
            const newData = {
              ...userInfoData,
              walletAddress: currentWalletAddress,
              pointsUserList: userPointsUserListData,
            };
            // 增加 posthog 用户信息：把平台 userId 绑定到 distinct_id，并附带基础属性
            if (userInfoData.userId && typeof window !== 'undefined' && posthog.__loaded) {
              posthog.identify(String(userInfoData.userId), {
                user_id: userInfoData.userId,
                name: userInfoData.name,
                username: userInfoData.username,
              });
            }
            // ✅ 同步更新 evmAddress 和 solanaAddress（保证与 userInfo 一致）
            set({
              userInfo: newData as unknown as UserProfile,
              userId: userInfo?.data?.userId as number,
              evmAddress: userInfoData.evmWalletAddress || null,
              solanaAddress: userInfoData.solWalletAddress || null,
            });
            // 平台登录后自动登录腾讯聊天 SDK
            if (userInfoData.trtcUserId && userInfoData.userSig) {
              useTrtcChatStore.getState().login(String(userInfoData.trtcUserId), userInfoData.userSig);
            }
            // 如果有邀请人，则设置邀请人信息
            if (!newData.inviter) {
              set({ userInfoLoading: false });
            }
          } else {
            set({ userInfoLoading: false });
          }
        } catch (err) {
          console.error('获取用户信息失败:', err);
          set({ userInfoLoading: false });
        }
      },
    }),
    {
      name: 'user-store', // 持久化存储的 key
    }
  )
);

// 跨标签页同步：监听 localStorage 变化，当其他标签页修改用户状态时自动同步
// 原理：当其他标签页修改 localStorage 时，会触发当前标签页的 storage 事件
// 这样就能实现：在一个标签页登录/登出时，其他标签页自动同步状态
// 注意：App WebView 环境下跳过，避免与 App 端登录状态冲突
if (typeof window !== 'undefined') {
  window.addEventListener('storage', e => {
    // App WebView 环境不参与跨标签页同步
    if (isInAppWebView()) return;
    // 只监听 user-store 的变化
    if (e.key === 'user-store' && e.newValue !== e.oldValue) {
      try {
        const currentState = useUserStore.getState();
        const oldToken = currentState.token;

        // 解析新的状态
        const newState = e.newValue ? JSON.parse(e.newValue) : null;
        const { state } = newState;
        // 如果state.token清空，或者e.newValue不存在，则清空直接登录
        if ((!state.token && state.token !== oldToken) || !e.newValue) {
          // useUserStore.getState().clearUserInfo();
          // api.resetToken('');
          // 如果当前不是登录页
          // if (window.location.pathname !== '/login') {
          //   window.location.href = '/login'
          // }
        }
      } catch (error) {
        console.error('跨标签页同步失败:', error);
      }
    }
  });
}

// 导出选择器函数，方便使用
export const useUserToken = () => {
  try {
    const token = useUserStore(state => state?.token);
    return token;
  } catch (error) {
    console.error('获取用户Token失败:', error);
    return '';
  }
};
export const useSolanaAddress = () => useUserStore(state => state.solanaAddress);
export const useEvmAddress = () => useUserStore(state => state.evmAddress);
export const useUserId = () => {
  try {
    return useUserStore(state => state?.userId);
  } catch (error) {
    console.error('获取用户ID失败:', error);
    return '';
  }
};
export const useIsLoggedIn = () => useUserStore(state => state.isLoggedIn());
export const useUserInfo = () => useUserStore(state => state.userInfo);
export const useUserInfoLoading = () => useUserStore(state => state.userInfoLoading);
export const useFetchUserInfo = () => useUserStore(state => state.fetchUserInfo);
